import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowUpDown, PackagePlus, Trash2, AlertTriangle, Loader2, ShieldCheck,
  Search, ChevronRight, ArrowRight, RefreshCw, Layers, CheckSquare, Square,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feb 2026 — Shared confirmation banner shown when the apply endpoint
// returns 409 `pallet_shipped_needs_confirmation`. Lists every
// placement whose new qty > 0 but whose shipped_state is still
// "shipped"/"consumed". The user ticks each "Pallet physically
// confirmed in the warehouse" row; `onConfirm` fires only when every
// warning row is ticked.
function ShippedConfirmBanner({
  detail, confirmedPalletIds, setConfirmedPalletIds,
  onCancel, onConfirm, busy, testIdPrefix,
}) {
  const warnings = (detail && detail.warnings) || [];
  const togglePallet = (pid) => {
    setConfirmedPalletIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const allTicked = warnings.length > 0 && warnings.every(
    (w) => w.pallet_id && confirmedPalletIds.has(w.pallet_id),
  );
  return (
    <div
      className="mt-3 rounded-lg bg-amber-50 border-2 border-amber-300 p-3"
      data-testid={`${testIdPrefix}-confirm-banner`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-bold text-amber-900">
            {warnings.length} pallet{warnings.length === 1 ? '' : 's'} marked as shipped/consumed
          </div>
          <div className="mt-1 text-xs text-amber-800 leading-relaxed">
            This adjustment will leave stock on these pallets, but the system
            thinks they were already dispatched. Confirm each one is{' '}
            <strong>physically in the warehouse</strong> before saving — the
            stale shipped flag will be cleared on save.
          </div>
          <ul className="mt-3 space-y-2 max-h-64 overflow-y-auto">
            {warnings.map((w, i) => {
              const key = w.pallet_id || `${w.placement_id}-${i}`;
              const ticked = w.pallet_id && confirmedPalletIds.has(w.pallet_id);
              return (
                <li
                  key={key}
                  className="rounded-md bg-white/70 border border-amber-200 px-3 py-2"
                  data-testid={`${testIdPrefix}-warning-row-${i}`}
                >
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!ticked}
                      onChange={() => w.pallet_id && togglePallet(w.pallet_id)}
                      disabled={!w.pallet_id}
                      className="mt-0.5 w-4 h-4"
                      data-testid={`${testIdPrefix}-warning-checkbox-${i}`}
                    />
                    <div className="flex-1 text-xs text-amber-900">
                      <div className="font-bold font-mono">
                        {w.pallet_id || '(no pallet id)'}
                      </div>
                      <div>
                        {w.product_name || w.product_id || '—'} ·{' '}
                        <span className="font-mono">{w.zone || '—'} / {w.location_id || '—'}</span>
                      </div>
                      <div>
                        new qty <strong>{w.new_qty}</strong> · state{' '}
                        <strong>{w.shipped_state}</strong>
                        {w.shipped_at ? ` on ${w.shipped_at}` : ''}
                      </div>
                      <div>
                        shipped by order{' '}
                        {w.shipped_order_found ? (
                          <>
                            <strong className="font-mono">{w.shipped_order_number || w.shipped_order_id}</strong>
                            {w.shipped_order_status
                              ? <> ({w.shipped_order_status})</>
                              : null}
                          </>
                        ) : (
                          w.shipped_order_id
                            ? <>
                                <strong className="font-mono">{w.shipped_order_id}</strong> (order not found)
                              </>
                            : <em>no shipped order on record</em>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11px] text-amber-800">
                        Pallet physically confirmed in the warehouse
                      </div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => onConfirm(confirmedPalletIds)}
              disabled={busy || !allTicked}
              className="px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-bold hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`${testIdPrefix}-confirm-apply-btn`}
            >
              {allTicked
                ? 'Confirm and apply — clears shipped flag on save'
                : `Tick all ${warnings.length} pallet(s) to enable`}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-50"
              data-testid={`${testIdPrefix}-cancel-btn`}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Feb 2026 — Stock Adjustment console (super admin + `warehouse.stock_adjust`).
 *
 * Placement-level writes that flow through
 * `POST /admin/warehouse/stock-adjustments`. The UI mirrors the
 * backend contract exactly:
 *   * Reason code is a mandatory dropdown.
 *   * Non-super users who try to reduce a placement below its
 *     allocated qty get a 409 with the affected orders; the modal
 *     re-prompts with a confirmation checkbox and resubmits.
 *   * Super admin see the same list but the release runs
 *     unconditionally.
 */
export default function AdminStockAdjust() {
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('mr_admin_token') || ''}` }),
    [],
  );
  const [meLoaded, setMeLoaded] = useState(false);
  const [hasPerm, setHasPerm] = useState(false);
  const [isSuper, setIsSuper] = useState(false);
  // Feb 2026 — Request-flow mode. `canApply === false` && `canRequest === true`
  // means the user must submit for approval; the same UI/endpoint responds
  // with `{status: 'pending'}` instead of applying.
  const [canApply, setCanApply] = useState(false);
  const [canRequest, setCanRequest] = useState(false);
  // Feb 2026 — Granular gates for the "New Pallet ID" / "New Location ID"
  // inputs. Super admin + `warehouse` wildcard implicitly pass; explicit
  // slugs are `warehouse.stock_adjust_pallet` / `_location`. Backend
  // rejects any adjustment that would rewrite those fields without the
  // matching perm — the UI just hides the inputs so users don't hit a
  // 403 after typing a value they thought was allowed.
  const [canEditPallet, setCanEditPallet] = useState(false);
  const [canEditLocation, setCanEditLocation] = useState(false);
  const requestMode = !canApply && canRequest;
  const [reasonCodes, setReasonCodes] = useState([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  const [busySearch, setBusySearch] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // placement being edited
  const [newQty, setNewQty] = useState('');
  const [newPalletId, setNewPalletId] = useState('');
  const [newLocationId, setNewLocationId] = useState('');
  const [pendingConfirm, setPendingConfirm] = useState(null); // { detail } on 409
  // Feb 2026 — second kind of 409: a shipped-state pallet needs
  // physical confirmation before its on-hand qty can be left > 0.
  // `shippedPendingConfirm.warnings` carries the per-pallet payload;
  // `confirmedPalletIds` tracks which checkboxes the user has ticked.
  const [shippedPendingConfirm, setShippedPendingConfirm] = useState(null);
  const [confirmedPalletIds, setConfirmedPalletIds] = useState(() => new Set());
  const [batchShippedPendingConfirm, setBatchShippedPendingConfirm] = useState(null);
  const [batchConfirmedPalletIds, setBatchConfirmedPalletIds] = useState(() => new Set());
  const [busySubmit, setBusySubmit] = useState(false);
  // Feb 2026 — Multi-select batch flow. Users can tick multiple
  // placements from the results list and adjust them in a single
  // request (backend already accepts `operations: [...]`). Each
  // selected placement carries its own target qty; a "Zero all"
  // shortcut sets every checked row's target to 0 for bulk write-
  // offs (damage, found-empty).
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [batchQtyById, setBatchQtyById] = useState({}); // placement_id -> string
  const [batchPendingConfirm, setBatchPendingConfirm] = useState(null);
  const [busyBatchSubmit, setBusyBatchSubmit] = useState(false);
  // Feb 2026 — pallet-consolidation button state.
  const [busyConsolidate, setBusyConsolidate] = useState(null);

  // -------- perm gate --------
  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/admin/me`, { headers: authHeaders });
        const superFlag = !!me.data?.is_super_admin;
        setIsSuper(superFlag);
        const perms = Array.isArray(me.data?.permissions) ? me.data.permissions : [];
        const wildcard = perms.includes('*');
        const bareWarehouse = perms.includes('warehouse');
        const apply = superFlag || wildcard || bareWarehouse || perms.includes('warehouse.stock_adjust');
        const request = perms.includes('warehouse.stock_adjust_request');
        const editPallet = superFlag || wildcard || bareWarehouse || perms.includes('warehouse.stock_adjust_pallet');
        const editLocation = superFlag || wildcard || bareWarehouse || perms.includes('warehouse.stock_adjust_location');
        setCanApply(apply);
        setCanRequest(request);
        setCanEditPallet(editPallet);
        setCanEditLocation(editLocation);
        setHasPerm(apply || request);
      } catch { /* leave defaults */ }
      setMeLoaded(true);
    })();
  }, [authHeaders]);

  // -------- reason codes --------
  useEffect(() => {
    if (!hasPerm) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/warehouse/stock-adjustments/reason-codes`, { headers: authHeaders });
        setReasonCodes(r.data?.reason_codes || []);
      } catch (e) {
        toast.error('Could not load reason codes');
      }
    })();
  }, [hasPerm, authHeaders]);

  // -------- placement search --------
  const runSearch = async () => {
    const q = (search || '').trim();
    if (!q) return;
    setBusySearch(true);
    try {
      const r = await axios.get(`${API}/admin/floor/stock-check`, {
        headers: authHeaders,
        // Feb 2026 — Adjust flow targets a specific placement_id, so
        // we opt out of the endpoint's default pallet-grouping. Every
        // physical placement becomes its own selectable row.
        //
        // Feb 2026 (user directive) — `include_zero=true` so items
        // whose on-hand reads ZERO (e.g. a fully-depleted rack for
        // MFG0038EX we just physically found some cartons of) still
        // surface as selectable rows. Without it the admin cannot
        // raise an adjustment to ADD stock for a zero-balance SKU.
        //
        // Feb 2026 — bumped `limit` to 2000 (server ceiling for the
        // `placement` grouping mode) so "Select all" can sweep every
        // matching placement in one submit instead of stopping at the
        // first 30. Users with more matches must narrow the query.
        params: { q, limit: 2000, group_by: 'placement', include_zero: true },
      });
      setResults(r.data?.results || []);
      // Reset multi-select whenever a new result set replaces the old
      // one — otherwise stale placement_ids would leak into the batch.
      setCheckedIds(new Set());
      setBatchQtyById({});
      setBatchPendingConfirm(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally {
      setBusySearch(false);
    }
  };

  // -------- Feb 2026 — Consolidate duplicate placements for a pallet --------
  // Called from the amber "duplicate" banner in the results list when the
  // current search returns ≥2 rows sharing the same pallet_id. Merges the
  // Stage/held row(s) into the Rack row via the backend endpoint and re-runs
  // the search so the admin can see the single surviving row.
  const consolidatePallet = async (palletId) => {
    if (!palletId) return;
    const ok = window.confirm(
      `Consolidate placements for pallet ${palletId}?\n\n` +
      `Any Stage remnants will be MERGED into the Rack row (allocations repointed, ` +
      `Stage placement deleted). This action is logged in the adjustment audit.`,
    );
    if (!ok) return;
    setBusyConsolidate(palletId);
    try {
      const { data } = await axios.post(
        `${API}/admin/warehouse/placements/consolidate-pallet`,
        { pallet_id: palletId },
        { headers: authHeaders },
      );
      if ((data.merges || []).length === 0) {
        toast.info(`No Stage remnants to merge for ${palletId}. ${(data.remaining_placements || []).length} row(s) already in-place.`);
      } else {
        const totalQty = data.merges.reduce((s, m) => s + (m.qty_merged || 0), 0);
        toast.success(`Consolidated ${data.merges.length} Stage row(s) for ${palletId}, merged qty ${totalQty}.`);
      }
      await runSearch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Consolidate failed for ${palletId}`);
    } finally {
      setBusyConsolidate(null);
    }
  };


  // -------- Feb 2026 — Multi-select batch helpers --------
  const allChecked = results.length > 0 && results.every((r) => checkedIds.has(r.placement_id));
  const someChecked = checkedIds.size > 0 && !allChecked;
  const toggleOne = (r) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(r.placement_id)) {
        next.delete(r.placement_id);
      } else {
        next.add(r.placement_id);
        // Prefill target qty with the current on-hand so the row
        // doesn't submit an accidental 0 delta.
        setBatchQtyById((m) => ({ ...m, [r.placement_id]: String(r.on_hand || 0) }));
      }
      return next;
    });
  };
  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set());
      return;
    }
    const next = new Set(results.map((r) => r.placement_id));
    const nextQty = { ...batchQtyById };
    for (const r of results) {
      if (nextQty[r.placement_id] === undefined) {
        nextQty[r.placement_id] = String(r.on_hand || 0);
      }
    }
    setCheckedIds(next);
    setBatchQtyById(nextQty);
  };
  const zeroAllSelected = () => {
    const next = { ...batchQtyById };
    for (const pid of checkedIds) next[pid] = '0';
    setBatchQtyById(next);
  };
  const setBatchQtyFor = (pid, val) => {
    setBatchQtyById((m) => ({ ...m, [pid]: val }));
  };

  const batchSubmit = async ({ confirm = false, confirmedPallets = null } = {}) => {
    if (checkedIds.size === 0) return;
    if (!reason) {
      toast.error('Pick a reason code first.');
      return;
    }
    // Build operations list from checked rows. Skip rows whose qty
    // is blank / non-numeric so users don't accidentally 0-out.
    const selectedRows = results.filter((r) => checkedIds.has(r.placement_id));
    const ops = [];
    for (const r of selectedRows) {
      const raw = batchQtyById[r.placement_id];
      const target = Number(raw);
      if (raw === undefined || raw === '' || !Number.isFinite(target) || target < 0) {
        toast.error(`Enter a non-negative target qty for ${r.product?.sku || r.placement_id}.`);
        return;
      }
      ops.push({ op: 'set', placement_id: r.placement_id, qty: target });
    }
    setBusyBatchSubmit(true);
    try {
      const palletsAck = Array.from(
        confirmedPallets !== null ? confirmedPallets : batchConfirmedPalletIds,
      );
      const { data } = await axios.post(
        `${API}/admin/warehouse/stock-adjustments`,
        {
          reason_code: reason,
          note,
          confirm_release_orders: !!confirm,
          physically_confirmed_pallet_ids: palletsAck,
          operations: ops,
        },
        { headers: authHeaders },
      );
      if (data?.status === 'pending') {
        toast.success(`Batch of ${ops.length} adjustment(s) submitted — awaiting approval (${(data.request_id || '').slice(0, 8)}).`);
        if ((data.affected_orders || []).length) {
          toast.warning(`Approver will release ${data.affected_orders.length} affected order(s) on approve.`);
        }
      } else {
        const applied = (data?.results || []).length;
        toast.success(`Batch applied — ${applied} placement(s) adjusted.`);
        if (data?.orders_released_and_replanned?.length) {
          toast.warning(`Released ${data.orders_released_and_replanned.length} affected order(s); auto re-planned.`);
        }
      }
      setBatchPendingConfirm(null);
      setBatchShippedPendingConfirm(null);
      setBatchConfirmedPalletIds(new Set());
      setCheckedIds(new Set());
      setBatchQtyById({});
      setNote('');
      await runSearch();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det && typeof det === 'object' && det.code === 'open_orders_would_be_released') {
        setBatchPendingConfirm(det);
      } else if (e?.response?.status === 409 && det && typeof det === 'object' && det.code === 'pallet_shipped_needs_confirmation') {
        setBatchShippedPendingConfirm(det);
        setBatchConfirmedPalletIds(new Set());
      } else {
        toast.error(typeof det === 'string' ? det : (det?.message || 'Batch adjustment failed'));
      }
    } finally {
      setBusyBatchSubmit(false);
    }
  };


  // -------- submit --------
  const submit = async ({ confirm = false, confirmedPallets = null } = {}) => {
    if (!selected) return;
    if (!reason) {
      toast.error('Pick a reason code first.');
      return;
    }
    const target = Number(newQty);
    if (!Number.isFinite(target) || target < 0) {
      toast.error('Enter a non-negative target qty.');
      return;
    }
    setBusySubmit(true);
    try {
      const op = { op: 'set', placement_id: selected.placement_id, qty: target };
      const nextLoc = (newLocationId || '').trim();
      const nextPal = (newPalletId || '').trim();
      if (nextLoc) op.location_id = nextLoc;
      if (nextPal) op.pallet_id = nextPal;
      const palletsAck = Array.from(
        confirmedPallets !== null ? confirmedPallets : confirmedPalletIds,
      );
      const { data } = await axios.post(
        `${API}/admin/warehouse/stock-adjustments`,
        {
          reason_code: reason,
          note,
          confirm_release_orders: !!confirm,
          physically_confirmed_pallet_ids: palletsAck,
          operations: [op],
        },
        { headers: authHeaders },
      );
      if (data?.status === 'pending') {
        toast.success(`Request submitted — awaiting approval (${(data.request_id || '').slice(0, 8)}).`);
        if ((data.affected_orders || []).length) {
          toast.warning(`Approver will release ${data.affected_orders.length} affected order(s) on approve.`);
        }
      } else {
        const r = data?.results?.[0] || {};
        if (r.merged) {
          toast.success(`Pallet merged successfully into existing location — combined qty ${r.merged_into_qty}.`);
        } else {
          toast.success(`${selected.product?.sku || 'Placement'}: ${r.before_qty} → ${r.after_qty}`);
        }
        if (data?.orders_released_and_replanned?.length) {
          toast.warning(`Released ${data.orders_released_and_replanned.length} affected order(s); auto re-planned.`);
        }
      }
      setPendingConfirm(null);
      setShippedPendingConfirm(null);
      setConfirmedPalletIds(new Set());
      setSelected(null);
      setNewQty('');
      setNewPalletId('');
      setNewLocationId('');
      setNote('');
      await runSearch();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det && typeof det === 'object' && det.code === 'open_orders_would_be_released') {
        // Prompt confirmation — permissioned user path.
        setPendingConfirm(det);
      } else if (e?.response?.status === 409 && det && typeof det === 'object' && det.code === 'pallet_shipped_needs_confirmation') {
        setShippedPendingConfirm(det);
        setConfirmedPalletIds(new Set());
      } else {
        toast.error(typeof det === 'string' ? det : (det?.message || 'Adjustment failed'));
      }
    } finally {
      setBusySubmit(false);
    }
  };

  if (!meLoaded) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center gap-2 text-slate-500 text-sm" data-testid="stock-adjust-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </AdminLayout>
    );
  }
  if (!hasPerm) {
    return (
      <AdminLayout>
        <div className="p-6 max-w-lg mx-auto text-center" data-testid="stock-adjust-no-perm">
          <ShieldCheck className="w-12 h-12 mx-auto text-slate-300" />
          <h1 className="mt-3 text-lg font-bold text-slate-900">Stock Adjustment</h1>
          <p className="mt-1 text-sm text-slate-500">
            You don't have permission to submit or apply stock adjustments. Ask a super admin to grant <code className="text-xs bg-slate-100 px-1 rounded">warehouse.stock_adjust_request</code> (submit for approval) or <code className="text-xs bg-slate-100 px-1 rounded">warehouse.stock_adjust</code> (apply directly).
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="stock-adjust-page">
        <div className="max-w-4xl mx-auto">
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
              <ArrowUpDown className="w-6 h-6 text-[#0078D4]" /> Stock Adjustment
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Placement-level count corrections, write-offs, and found stock. Every adjustment writes to the audit ledger + recomputes the product summary from placements.
            </p>
          </div>
          {isSuper && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider">
              <ShieldCheck className="w-3 h-3" /> Super admin
            </span>
          )}
          {requestMode && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider" data-testid="stock-adjust-request-mode-badge">
              <AlertTriangle className="w-3 h-3" /> Approval required
            </span>
          )}
        </div>

        {requestMode && (
          <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900" data-testid="stock-adjust-request-mode-hint">
            You can submit stock adjustments <b>for review</b>. Nothing changes on the floor until a manager approves your request.
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
            Find a placement
          </label>
          <div className="mt-1 flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                placeholder="Scan pallet, or type SKU / product name / location"
                className="w-full pl-9 pr-3 h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none text-sm"
                data-testid="stock-adjust-search-input"
              />
            </div>
            <button
              type="button"
              onClick={runSearch}
              disabled={busySearch || !search}
              className="px-4 h-11 rounded-lg bg-[#0078D4] text-white text-sm font-semibold hover:bg-[#005A9E] disabled:opacity-50"
              data-testid="stock-adjust-search-btn"
            >
              {busySearch ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </button>
          </div>
          {/* Results list */}
          {results.length > 0 && (
            <div className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-100 max-h-80 overflow-y-auto" data-testid="stock-adjust-results">
              {/* Feb 2026 — Multi-select header. "Select all" master
                  checkbox lets users tick every row in the current
                  result set, then use the batch panel below to apply
                  a bulk qty change (e.g. write-off multiple damaged
                  pallets in one request). */}
              <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 sticky top-0 z-10" data-testid="stock-adjust-select-all-row">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 hover:text-[#0078D4]"
                  data-testid="stock-adjust-select-all-btn"
                  aria-checked={allChecked ? 'true' : someChecked ? 'mixed' : 'false'}
                  role="checkbox"
                >
                  {allChecked ? (
                    <CheckSquare className="w-4 h-4 text-[#0078D4]" />
                  ) : someChecked ? (
                    <CheckSquare className="w-4 h-4 text-[#0078D4] opacity-60" />
                  ) : (
                    <Square className="w-4 h-4 text-slate-400" />
                  )}
                  Select all ({results.length})
                </button>
                {checkedIds.size > 0 && (
                  <span className="ml-auto text-[11px] font-bold text-[#0078D4] tabular-nums" data-testid="stock-adjust-selected-count">
                    {checkedIds.size} selected
                  </span>
                )}
              </div>
              {/* Feb 2026 — pallet-consolidation banner. When ≥2 rows in
                  the current results share the same pallet_id, super
                  admin gets a one-click "Consolidate duplicates" that
                  merges Stage remnants into the Rack row via
                  POST /admin/warehouse/placements/consolidate-pallet. */}
              {isSuper && (() => {
                const byPallet = {};
                for (const r of results) {
                  const p = (r.pallet_id || '').trim();
                  if (!p) continue;
                  byPallet[p] = (byPallet[p] || 0) + 1;
                }
                const dupes = Object.entries(byPallet).filter(([, n]) => n > 1);
                if (dupes.length === 0) return null;
                return (
                  <div className="p-3 bg-amber-50 flex items-start gap-2" data-testid="stock-adjust-dupe-banner">
                    <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 text-xs">
                      <div className="font-bold text-amber-900">
                        Multiple placements for pallet {dupes.map(([p]) => p).join(', ')}
                      </div>
                      <div className="mt-0.5 text-amber-800">
                        If one is a leftover Stage row, merge it into the Rack row (repoints allocations, deletes the Stage placement).
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {dupes.map(([p]) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => consolidatePallet(p)}
                          disabled={busyConsolidate === p}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-700 text-white text-[11px] font-bold hover:bg-amber-800 disabled:opacity-50"
                          data-testid={`stock-adjust-consolidate-${p}`}
                        >
                          {busyConsolidate === p && <Loader2 className="w-3 h-3 animate-spin" />}
                          <Layers className="w-3 h-3" />
                          Consolidate {p}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {results.map((r) => {
                const isChecked = checkedIds.has(r.placement_id);
                return (
                <div
                  key={r.placement_id}
                  className={`w-full flex items-center gap-3 px-3 py-2 ${selected?.placement_id === r.placement_id ? 'bg-[#DEECF9]' : isChecked ? 'bg-blue-50/40' : 'hover:bg-slate-50'}`}
                  data-testid={`stock-adjust-result-${r.placement_id}`}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleOne(r); }}
                    className="p-1 -m-1 shrink-0"
                    data-testid={`stock-adjust-check-${r.placement_id}`}
                    aria-checked={isChecked ? 'true' : 'false'}
                    role="checkbox"
                  >
                    {isChecked ? (
                      <CheckSquare className="w-4 h-4 text-[#0078D4]" />
                    ) : (
                      <Square className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSelected(r); setNewQty(String(r.on_hand || 0)); setNewPalletId(''); setNewLocationId(''); setPendingConfirm(null); }}
                    className="flex-1 flex items-center gap-3 text-left"
                    data-testid={`stock-adjust-open-${r.placement_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs flex-wrap">
                        <span className="font-mono font-bold text-[#0078D4]">{r.product?.sku || '—'}</span>
                        <span className="text-slate-900 truncate">{r.product?.name || ''}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500 tabular-nums flex gap-2 flex-wrap">
                        <span>Pallet <b>{r.pallet_id || '—'}</b></span>
                        <span>Loc <b>{r.location_id || '—'}</b></span>
                        <span>Qty <b>{r.on_hand || 0}</b></span>
                        <span className="text-amber-700">Alloc <b>{r.allocated || 0}</b></span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                  {isSuper && (
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        const isHeld = !!r.hold_pallet;
                        const promptMsg = isHeld
                          ? `Release hold on pallet ${r.pallet_id || r.placement_id}?\nReason (optional):`
                          : `HOLD pallet ${r.pallet_id || r.placement_id}?\nIt will be excluded from every allocation path across all divisions.\nReason (required):`;
                        const reason = window.prompt(promptMsg, isHeld ? '' : '');
                        if (reason === null) return;
                        if (!isHeld && !reason.trim()) {
                          toast.error('Reason is required to place a pallet on hold.');
                          return;
                        }
                        try {
                          const url = isHeld
                            ? `${API}/admin/data-tools/release-placement-hold`
                            : `${API}/admin/data-tools/place-placement-hold`;
                          await axios.post(
                            url,
                            { placement_id: r.placement_id, commit: true, reason: reason.trim() },
                            { headers: authHeaders },
                          );
                          toast.success(isHeld ? 'Hold released.' : 'Pallet is now on hold.');
                          await runSearch();
                        } catch (err) {
                          const d = err?.response?.data?.detail;
                          toast.error(typeof d === 'string' ? d : (d?.message || err.message || 'Hold toggle failed.'));
                        }
                      }}
                      className={`shrink-0 inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-bold uppercase tracking-wider border ${
                        r.hold_pallet
                          ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                          : 'bg-white text-rose-700 border-rose-300 hover:bg-rose-50'
                      }`}
                      data-testid={`stock-adjust-hold-toggle-${r.placement_id}`}
                      title={r.hold_pallet ? 'Release this pallet (super admin)' : 'Put this pallet on hold (super admin). Blocks every allocation across all divisions.'}
                    >
                      {r.hold_pallet ? 'On hold · release' : 'Hold'}
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Feb 2026 — Multi-select batch panel. Appears whenever ≥1
            placement is ticked. Shares one reason + note across the
            whole batch, and lets users either zero out all selected
            rows in one click (bulk write-off) or set each row's
            target qty individually. Submits every op in one call. */}
        {checkedIds.size > 0 && (
          <div className="bg-white rounded-xl border-2 border-[#0078D4] p-4 mb-4" data-testid="stock-adjust-batch-panel">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-[#0078D4]" />
                Batch adjustment
                <span className="text-[11px] font-bold text-[#0078D4] tabular-nums bg-blue-50 px-2 py-0.5 rounded-full">
                  {checkedIds.size} placement{checkedIds.size > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={zeroAllSelected}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-50 border border-rose-200 text-rose-700 text-[11px] font-bold hover:bg-rose-100"
                  data-testid="stock-adjust-zero-all-btn"
                  title="Set every selected placement's target qty to 0 (bulk write-off)"
                >
                  <Trash2 className="w-3 h-3" />
                  Zero all selected
                </button>
                <button
                  type="button"
                  onClick={() => { setCheckedIds(new Set()); setBatchQtyById({}); setBatchPendingConfirm(null); }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-slate-700 text-[11px] font-bold hover:bg-slate-50"
                  data-testid="stock-adjust-clear-batch-btn"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="stock-adjust-batch-rows">
              {results.filter((r) => checkedIds.has(r.placement_id)).map((r) => {
                const cur = Number(r.on_hand || 0);
                const raw = batchQtyById[r.placement_id];
                const tgt = raw === undefined || raw === '' ? null : Number(raw);
                const delta = tgt === null || Number.isNaN(tgt) ? null : tgt - cur;
                const belowAlloc = tgt !== null && Number(r.allocated || 0) > 0 && tgt < Number(r.allocated || 0);
                return (
                  <div key={r.placement_id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 text-xs" data-testid={`stock-adjust-batch-row-${r.placement_id}`}>
                    <div className="col-span-6 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-[#0078D4]">{r.product?.sku || '—'}</span>
                        <span className="text-slate-900 truncate">{r.product?.name || ''}</span>
                      </div>
                      <div className="mt-0.5 text-[10px] text-slate-500 tabular-nums flex gap-2 flex-wrap">
                        <span>Pallet <b>{r.pallet_id || '—'}</b></span>
                        <span>Loc <b>{r.location_id || '—'}</b></span>
                        <span>Cur <b>{cur}</b></span>
                        <span className="text-amber-700">Alloc <b>{r.allocated || 0}</b></span>
                      </div>
                    </div>
                    <div className="col-span-3">
                      <input
                        type="number"
                        min="0"
                        value={raw === undefined ? String(cur) : raw}
                        onChange={(e) => setBatchQtyFor(r.placement_id, e.target.value)}
                        className="w-full h-9 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-2 text-xs tabular-nums font-bold text-right"
                        data-testid={`stock-adjust-batch-qty-${r.placement_id}`}
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-1 justify-end tabular-nums">
                      {delta !== null && !Number.isNaN(delta) && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            delta > 0
                              ? 'text-emerald-800 bg-emerald-100'
                              : delta < 0
                                ? 'text-rose-800 bg-rose-100'
                                : 'text-slate-700 bg-slate-100'
                          }`}
                        >
                          {delta >= 0 ? '+' : ''}{delta}
                        </span>
                      )}
                      {belowAlloc && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-800 bg-amber-100"
                          title="New qty is below current allocated qty — approval will release orders."
                          data-testid={`stock-adjust-batch-warn-${r.placement_id}`}
                        >
                          !
                        </span>
                      )}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => toggleOne(r)}
                        className="p-1 text-slate-400 hover:text-rose-600"
                        data-testid={`stock-adjust-batch-remove-${r.placement_id}`}
                        title="Remove from batch"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Reason code <span className="text-rose-600">*</span></span>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm bg-white"
                  data-testid="stock-adjust-batch-reason-select"
                >
                  <option value="">— Select reason —</option>
                  {reasonCodes.map((c) => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Note (optional)</span>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. damaged in transit — bulk write-off"
                  className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm"
                  data-testid="stock-adjust-batch-note-input"
                />
              </label>
            </div>

            {batchPendingConfirm && (
              <div className="mt-3 rounded-lg bg-amber-50 border-2 border-amber-300 p-3" data-testid="stock-adjust-batch-confirm-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-amber-900">
                      This batch will release {batchPendingConfirm.affected_orders?.length || 0} open order(s).
                    </div>
                    <div className="mt-1 text-xs text-amber-800">
                      {requestMode
                        ? 'When your request is approved, these orders will be released and automatically re-planned onto the surviving stock. Tick to acknowledge before submitting.'
                        : 'The affected orders will be released and automatically re-planned onto the surviving stock.'}
                    </div>
                    <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                      {(batchPendingConfirm.affected_orders || []).map((o) => (
                        <li key={o.order_id} className="text-xs font-mono text-amber-900">
                          #{o.order_number || o.order_id?.slice(0, 8)} — {o.qty_held} held
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => batchSubmit({ confirm: true })}
                        disabled={busyBatchSubmit}
                        className="px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-bold hover:bg-amber-800 disabled:opacity-50"
                        data-testid="stock-adjust-batch-confirm-release-btn"
                      >
                        {requestMode
                          ? `I understand — submit batch to release ${batchPendingConfirm.affected_orders?.length || 0} order(s)`
                          : 'Release & apply batch'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setBatchPendingConfirm(null)}
                        className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!batchPendingConfirm && batchShippedPendingConfirm && (
              <ShippedConfirmBanner
                detail={batchShippedPendingConfirm}
                confirmedPalletIds={batchConfirmedPalletIds}
                setConfirmedPalletIds={setBatchConfirmedPalletIds}
                onCancel={() => { setBatchShippedPendingConfirm(null); setBatchConfirmedPalletIds(new Set()); }}
                onConfirm={(set) => { batchSubmit({ confirmedPallets: set }); }}
                busy={busyBatchSubmit}
                testIdPrefix="stock-adjust-batch-shipped"
              />
            )}

            {!batchPendingConfirm && !batchShippedPendingConfirm && (
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => batchSubmit()}
                  disabled={busyBatchSubmit || !reason || checkedIds.size === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0078D4] text-white text-sm font-bold hover:bg-[#005A9E] disabled:opacity-50"
                  data-testid="stock-adjust-batch-submit-btn"
                >
                  {busyBatchSubmit && <Loader2 className="w-4 h-4 animate-spin" />}
                  {requestMode
                    ? `Submit ${checkedIds.size} for approval`
                    : `Apply ${checkedIds.size} adjustment${checkedIds.size > 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Adjustment form */}
        {selected && (
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stock-adjust-form">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Record line — full details</div>
            {/* Feb 2026 — user directive: "record lines must show on the
                user's screen with full information". Every field the
                requester needs to double-check before submitting for
                approval renders here: SKU + full item name + pack/UOM +
                batch/expiry + pallet/location + current/allocated/
                available + live before→after preview + delta chip. */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-3" data-testid="stock-adjust-record-line">
              <div className="flex items-start gap-2 flex-wrap">
                <span className="font-mono font-bold text-[#0078D4] text-sm" data-testid="stock-adjust-record-sku">
                  {selected.product?.sku || '—'}
                </span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-900 text-sm font-medium" data-testid="stock-adjust-record-name">
                  {selected.product?.name || '(no name)'}
                </span>
                {(selected.product?.size || selected.product?.uom) && (
                  <span className="text-xs text-slate-500 ml-1" data-testid="stock-adjust-record-pack">
                    {selected.product?.size && <>({selected.product.size})</>}
                    {selected.product?.size && selected.product?.uom && ' · '}
                    {selected.product?.uom}
                  </span>
                )}
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
                <div>
                  <div className="uppercase tracking-wider font-bold text-slate-500 text-[9px]">Pallet</div>
                  <div className="mt-0.5 text-slate-900 font-mono">{selected.pallet_id || '—'}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wider font-bold text-slate-500 text-[9px]">Location</div>
                  <div className="mt-0.5 text-slate-900 font-mono">{selected.location_id || '—'}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wider font-bold text-slate-500 text-[9px]">Batch</div>
                  <div className="mt-0.5 text-slate-900 font-mono">{selected.batch_ref || '—'}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wider font-bold text-slate-500 text-[9px]">Expiry</div>
                  <div
                    className={`mt-0.5 font-mono ${
                      selected.expiry_date && new Date(selected.expiry_date) < new Date()
                        ? 'text-rose-700 font-bold'
                        : 'text-slate-900'
                    }`}
                  >
                    {selected.expiry_date ? String(selected.expiry_date).slice(0, 10) : '—'}
                  </div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-3 gap-3 text-[11px] tabular-nums" data-testid="stock-adjust-record-qtys">
                <div>
                  <div className="uppercase tracking-wider font-bold text-slate-500 text-[9px]">Current qty</div>
                  <div className="mt-0.5 text-slate-900 font-bold text-sm">{selected.on_hand || 0}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wider font-bold text-slate-500 text-[9px]">Allocated</div>
                  <div className="mt-0.5 text-amber-700 font-bold text-sm">{selected.allocated || 0}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wider font-bold text-slate-500 text-[9px]">Available</div>
                  <div className="mt-0.5 text-emerald-700 font-bold text-sm">
                    {Math.max(0, Number(selected.on_hand || 0) - Number(selected.allocated || 0))}
                  </div>
                </div>
              </div>
              {/* Live before → after preview. Redraws as the target
                  qty input changes so the requester sees the impact
                  of the number they're about to submit. */}
              {newQty !== '' && !Number.isNaN(Number(newQty)) && (
                <div
                  className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2 text-[11px]"
                  data-testid="stock-adjust-record-preview"
                >
                  <span className="uppercase tracking-wider font-bold text-slate-500 text-[9px]">Preview</span>
                  <span className="font-mono tabular-nums text-slate-900 font-bold">{selected.on_hand || 0}</span>
                  <ArrowRight className="w-3 h-3 text-slate-400" />
                  <span
                    className={`font-mono tabular-nums font-bold ${
                      Number(newQty) - Number(selected.on_hand || 0) > 0
                        ? 'text-emerald-700'
                        : Number(newQty) - Number(selected.on_hand || 0) < 0
                          ? 'text-rose-700'
                          : 'text-slate-700'
                    }`}
                  >
                    {newQty}
                  </span>
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      Number(newQty) - Number(selected.on_hand || 0) > 0
                        ? 'text-emerald-800 bg-emerald-100'
                        : Number(newQty) - Number(selected.on_hand || 0) < 0
                          ? 'text-rose-800 bg-rose-100'
                          : 'text-slate-700 bg-slate-100'
                    }`}
                  >
                    {Number(newQty) - Number(selected.on_hand || 0) >= 0 ? '+' : ''}
                    {Number(newQty) - Number(selected.on_hand || 0)}
                  </span>
                  {(Number(selected.allocated || 0) > 0
                    && Number(newQty) < Number(selected.allocated || 0)) && (
                    <span
                      className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-800 bg-amber-100"
                      data-testid="stock-adjust-record-warn-allocated"
                      title="New qty is below current allocated qty — approval will release orders."
                    >
                      Below allocated ({selected.allocated})
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Set qty to</span>
                  <input
                    type="number"
                    min="0"
                    value={newQty}
                    onChange={(e) => setNewQty(e.target.value)}
                    className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm tabular-nums font-bold"
                    data-testid="stock-adjust-newqty-input"
                  />
                </label>
                <div className="mt-1 text-[11px] text-slate-500 tabular-nums">
                  Delta: <b className={Number(newQty) - Number(selected.on_hand || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}>
                    {Number(newQty) - Number(selected.on_hand || 0) >= 0 ? '+' : ''}{Number(newQty) - Number(selected.on_hand || 0)}
                  </b>
                </div>
              </div>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="stock-adjust-relabel-fields">
              {canEditPallet && (
                <label className="block" data-testid="stock-adjust-new-pallet-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    New pallet ID <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={newPalletId}
                    onChange={(e) => setNewPalletId(e.target.value)}
                    placeholder={selected.pallet_id ? `Current: ${selected.pallet_id}` : 'Leave blank to keep current'}
                    className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono"
                    data-testid="stock-adjust-new-pallet-input"
                  />
                </label>
              )}
              {canEditLocation && (
                <label className="block" data-testid="stock-adjust-new-location-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    New location ID <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={newLocationId}
                    onChange={(e) => setNewLocationId(e.target.value)}
                    placeholder={selected.location_id ? `Current: ${selected.location_id}` : 'Leave blank to keep current'}
                    className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono"
                    data-testid="stock-adjust-new-location-input"
                  />
                </label>
              )}
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Reason code <span className="text-rose-600">*</span></span>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm bg-white"
                  data-testid="stock-adjust-reason-select"
                >
                  <option value="">— Select reason —</option>
                  {reasonCodes.map((c) => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Note (optional)</span>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. floor count came up short"
                  className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm"
                  data-testid="stock-adjust-note-input"
                />
              </label>
            </div>

            {/* Confirmation banner (open-order safety) */}
            {pendingConfirm && (
              <div className="mt-3 rounded-lg bg-amber-50 border-2 border-amber-300 p-3" data-testid="stock-adjust-confirm-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-amber-900">
                      This reduction will release {pendingConfirm.affected_orders?.length || 0} open order(s).
                    </div>
                    <div className="mt-1 text-xs text-amber-800">
                      {requestMode
                        ? 'When your request is approved, these orders will be released and automatically re-planned onto the surviving stock. Tick to acknowledge before submitting.'
                        : 'The affected orders will be released and automatically re-planned onto the surviving stock.'}
                    </div>
                    <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                      {(pendingConfirm.affected_orders || []).map((o) => (
                        <li key={o.order_id} className="text-xs font-mono text-amber-900">
                          #{o.order_number || o.order_id?.slice(0, 8)} — {o.qty_held} on this pallet
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => submit({ confirm: true })}
                        disabled={busySubmit}
                        className="px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-bold hover:bg-amber-800 disabled:opacity-50"
                        data-testid="stock-adjust-confirm-release-btn"
                      >
                        {requestMode
                          ? `I understand — submit request to release ${pendingConfirm.affected_orders?.length || 0} order(s)`
                          : 'Release & adjust anyway'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingConfirm(null)}
                        className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!pendingConfirm && shippedPendingConfirm && (
              <ShippedConfirmBanner
                detail={shippedPendingConfirm}
                confirmedPalletIds={confirmedPalletIds}
                setConfirmedPalletIds={setConfirmedPalletIds}
                onCancel={() => { setShippedPendingConfirm(null); setConfirmedPalletIds(new Set()); }}
                onConfirm={(set) => { submit({ confirmedPallets: set }); }}
                busy={busySubmit}
                testIdPrefix="stock-adjust-shipped"
              />
            )}

            {!pendingConfirm && !shippedPendingConfirm && (
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => { setSelected(null); setNewQty(''); setNewPalletId(''); setNewLocationId(''); }}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => submit()}
                  disabled={busySubmit || !reason || newQty === ''}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0078D4] text-white text-sm font-bold hover:bg-[#005A9E] disabled:opacity-50"
                  data-testid="stock-adjust-submit-btn"
                >
                  {busySubmit && <Loader2 className="w-4 h-4 animate-spin" />}
                  {requestMode ? 'Submit for approval' : 'Apply adjustment'}
                </button>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </AdminLayout>
  );
}
